import { useState, useEffect } from 'react'
import axios from 'axios'

interface AddressSelectorProps {
  province: string
  district: string
  subdistrict: string
  postalCode: string
  onAddressChange: (field: string, value: string) => void
}

interface Subdistrict {
  district: string
  zipcode: number
}

export default function AddressSelector({
  province,
  district,
  subdistrict,
  postalCode,
  onAddressChange,
}: AddressSelectorProps) {
  const [provinces, setProvinces] = useState<string[]>([])
  const [districts, setDistricts] = useState<string[]>([])
  const [subdistricts, setSubdistricts] = useState<Subdistrict[]>([])

  // Load all address data on mount
  useEffect(() => {
    loadProvinces()
  }, [])

  // Load districts when province changes
  useEffect(() => {
    if (province) {
      loadDistricts(province)
    } else {
      setDistricts([])
      setSubdistricts([])
    }
  }, [province])

  // Load subdistricts when district changes
  useEffect(() => {
    if (province && district) {
      loadSubdistricts(province, district)
    } else {
      setSubdistricts([])
    }
  }, [province, district])

  const loadProvinces = async () => {
    try {
      const response = await axios.get(
        'https://raw.githubusercontent.com/earthchie/jquery.Thailand.js/master/jquery.Thailand.js/database/raw_database/raw_database.json'
      )
      const uniqueProvinces = [...new Set(response.data.map((item: any) => item.province))]
      setProvinces(uniqueProvinces as string[])
    } catch (error) {
      console.error('Error loading provinces:', error)
    }
  }

  const loadDistricts = async (selectedProvince: string) => {
    try {
      const response = await axios.get(
        'https://raw.githubusercontent.com/earthchie/jquery.Thailand.js/master/jquery.Thailand.js/database/raw_database/raw_database.json'
      )
      const filteredData = response.data.filter((item: any) => item.province === selectedProvince)
      const uniqueDistricts = [...new Set(filteredData.map((item: any) => item.amphoe))]
      setDistricts(uniqueDistricts as string[])
    } catch (error) {
      console.error('Error loading districts:', error)
    }
  }

  const loadSubdistricts = async (selectedProvince: string, selectedDistrict: string) => {
    try {
      const response = await axios.get(
        'https://raw.githubusercontent.com/earthchie/jquery.Thailand.js/master/jquery.Thailand.js/database/raw_database/raw_database.json'
      )
      const filteredData = response.data.filter(
        (item: any) => item.province === selectedProvince && item.amphoe === selectedDistrict
      )
      const subdistrictsData = filteredData.map((item: any) => ({
        district: item.district,
        zipcode: parseInt(item.zipcode),
      }))
      setSubdistricts(subdistrictsData)
    } catch (error) {
      console.error('Error loading subdistricts:', error)
    }
  }

  const handleProvinceChange = (value: string) => {
    onAddressChange('province', value)
    onAddressChange('district', '')
    onAddressChange('subdistrict', '')
    onAddressChange('postalCode', '')
  }

  const handleDistrictChange = (value: string) => {
    onAddressChange('district', value)
    onAddressChange('subdistrict', '')
    onAddressChange('postalCode', '')
  }

  const handleSubdistrictChange = (value: string) => {
    onAddressChange('subdistrict', value)
    // Auto-fill postal code
    const selected = subdistricts.find((s) => s.district === value)
    if (selected) {
      onAddressChange('postalCode', selected.zipcode.toString())
    }
  }

  return (
    <div className="space-y-4">
      {/* จังหวัด */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          จังหวัด <span className="text-error">*</span>
        </label>
        <select
          value={province}
          onChange={(e) => handleProvinceChange(e.target.value)}
          className="w-full h-input px-4 border border-border-medium rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-800"
          required
        >
          <option value="">เลือกจังหวัด</option>
          {provinces.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      {/* อำเภอ/เขต */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          อำเภอ/เขต <span className="text-error">*</span>
        </label>
        <select
          value={district}
          onChange={(e) => handleDistrictChange(e.target.value)}
          className="w-full h-input px-4 border border-border-medium rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-800"
          disabled={!province}
          required
        >
          <option value="">เลือกอำเภอ/เขต</option>
          {districts.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>

      {/* ตำบล/แขวง */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          ตำบล/แขวง <span className="text-error">*</span>
        </label>
        <select
          value={subdistrict}
          onChange={(e) => handleSubdistrictChange(e.target.value)}
          className="w-full h-input px-4 border border-border-medium rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-800"
          disabled={!district}
          required
        >
          <option value="">เลือกตำบล/แขวง</option>
          {subdistricts.map((s) => (
            <option key={s.district} value={s.district}>
              {s.district}
            </option>
          ))}
        </select>
      </div>

      {/* รหัสไปรษณีย์ */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          รหัสไปรษณีย์ <span className="text-error">*</span>
        </label>
        <input
          type="text"
          value={postalCode}
          onChange={(e) => onAddressChange('postalCode', e.target.value)}
          className="w-full h-input px-4 border border-border-medium rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-800"
          placeholder="รหัสไปรษณีย์"
          maxLength={5}
          readOnly
          required
        />
      </div>
    </div>
  )
}
